// react
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

// material ui
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';

// feature-components
import useValidateMeterRow from '@/features/energyxplorer/portfolio/components/meter-drawer/data-integrity/useValidateMeterRow';
// components
import {
  closeDialog,
  selectDataIntegrityOpen,
  selectDataIntegrityRow,
} from '@/features/energyxplorer/redux/dataIntegritySlice';
import { formatNumber } from '@/utils/formatters';

import { DateTime } from 'luxon';

const formatPercent = (value: number | null | undefined) =>
  `${formatNumber((value ?? 0) * 100)}%`;

const formatDateShort = (dateStr: string) => {
  if (!dateStr) return '';
  const dt = DateTime.fromISO(dateStr);
  return dt.isValid ? dt.toLocaleString(DateTime.DATE_SHORT) : dateStr;
};

function ValueCard({
  label,
  value,
  warnings = [],
}: {
  label: string;
  value: string;
  warnings?: { key: string; value: string }[];
}) {
  const [showDetails, setShowDetails] = useState(false);
  const clickable = warnings.length > 0;

  return (
    <Box>
      <Card
        onClick={clickable ? () => setShowDetails((prev) => !prev) : undefined}
        sx={{
          cursor: clickable ? 'pointer' : 'default',
          border: clickable ? 2 : 1,
          borderColor: clickable ? 'warning.main' : 'divider',
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="center">
            <Typography variant="caption" color={'text.secondary'}>
              {label}
            </Typography>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent={'space-between'}
          >
            <Typography variant="h6">{value}</Typography>
            {!clickable ? (
              <CheckCircleIcon color="success" />
            ) : (
              <ErrorIcon color="warning" />
            )}
          </Box>
          {clickable && (
            <Collapse in={showDetails}>
              <Box mt={2}>
                {warnings.map((w) => (
                  <Alert key={w.value} severity="warning">
                    {w.value}
                  </Alert>
                ))}
              </Box>
            </Collapse>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default function DataIntegrityDialog() {
  const { t } = useTranslation('portfolio-analysis');
  const dispatch = useDispatch();
  const open = useSelector(selectDataIntegrityOpen);
  const row = useSelector(selectDataIntegrityRow);
  const meter = row ?? {};

  const one_year_data_pct = meter.one_year_data_pct ?? 0;
  const two_year_data_pct = meter.two_year_data_pct ?? 0;
  const three_year_data_pct = meter.three_year_data_pct ?? 0;
  const thirty_day_data_pct = meter.thirty_day_data_pct ?? 0;
  const data_start_time = meter.data_start_time ?? '';
  const data_end_time = meter.data_end_time ?? '';
  const address = meter.address ?? '';

  const validateMeter = useValidateMeterRow();
  const warnings = validateMeter(meter);
  const getWarnings = (key: string) => {
    return warnings.filter((w) => w.key === key);
  };

  const cards = [
    {
      label: t('data_quality.30_days'),
      value: formatPercent(thirty_day_data_pct),
      warnings: getWarnings('thirty_day_data_pct'),
    },
    {
      label: t('data_quality.1_year'),
      value: formatPercent(one_year_data_pct),
      warnings: getWarnings('one_year_data_pct'),
    },
    {
      label: t('data_quality.2_year'),
      value: formatPercent(two_year_data_pct),
      warnings: getWarnings('two_year_data_pct'),
    },
    {
      label: t('data_quality.3_year'),
      value: formatPercent(three_year_data_pct),
      warnings: getWarnings('three_year_data_pct'),
    },
    {
      label: t('data_quality.period'),
      value: `${formatDateShort(data_start_time)} - ${formatDateShort(data_end_time)}`,
      warnings: getWarnings('data_end_time'),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={() => dispatch(closeDialog())}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        {t('data_quality.header')} - {address}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {cards.map((card) => (
            <Grid
              key={card.label}
              size={{
                xs: 12,
                sm: 12
              }}>
              <ValueCard
                label={card.label}
                value={card.value}
                warnings={card.warnings}
              />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => dispatch(closeDialog())}>
          {t('common.close', 'Luk')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
